import api from './api';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// // Create axios instance with default config
// const api = axios.create({
//   baseURL: API_URL,
//   withCredentials: true, // Important for session cookies
//   headers: {
//     'Content-Type': 'application/json',
//     'Accept': 'application/json',
//   },
// });

// // Request interceptor to add auth token if available
// api.interceptors.request.use(
//   (config) => {
//     // Add any auth headers here if needed
//     return config;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );

// // Response interceptor to handle common errors
// api.interceptors.response.use(
//   (response) => {
//     return response;
//   },
//   (error) => {
//     if (error.response?.status === 401) {
//       // Handle unauthorized access
//       localStorage.removeItem('user');
//       localStorage.removeItem('isAuthenticated');
//       // Don't redirect here - let the app handle it
//     }
//     return Promise.reject(error);
//   }
// );

// ---------------------------------------------------------------------------
// Schedule/targets cache (frontend)
// Makes Schedule tab feel instant by avoiding repeat network waits.
// ---------------------------------------------------------------------------
const scheduleTargetsCache = new Map(); // key -> { expiresAt, data }
const scheduleTargetsInFlight = new Map(); // key -> Promise<axiosResponse>
const SCHEDULE_TARGETS_CACHE_TTL_MS = 60 * 1000;
const getScheduleTargetsCacheKey = (practiceId) => `practice:${practiceId}`;

// ---------------------------------------------------------------------------
// Practice patients cache (frontend, session-scoped)
// Avoids repeated list fetches when users switch tabs/pages and come back.
// ---------------------------------------------------------------------------
const practicePatientsCache = new Map(); // key -> { data }
const practicePatientsInFlight = new Map(); // key -> Promise<axiosResponse>
let practicePatientsCacheGeneration = 0;
const programAnalyticsCache = new Map(); // key -> { data, ts }
const programAnalyticsInFlight = new Map(); // key -> Promise
const PROGRAM_ANALYTICS_CACHE_TTL_MS = 90_000;
const normalizeParams = (params = {}) => {
  return Object.keys(params)
    .sort()
    .reduce((acc, key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
};
const PRACTICE_PATIENTS_CACHE_VERSION = 'v11';
const getPracticePatientsCacheKey = (practiceId, params = {}) =>
  `${PRACTICE_PATIENTS_CACHE_VERSION}:practice:${practiceId}:patients:${JSON.stringify(normalizeParams(params))}`;

const storePracticePatientsCache = (cacheKey, generation, data) => {
  if (generation !== practicePatientsCacheGeneration) return;
  practicePatientsCache.set(cacheKey, { data });
};

const clearPracticePatientsInFlight = (cacheKey, promise) => {
  if (practicePatientsInFlight.get(cacheKey) === promise) {
    practicePatientsInFlight.delete(cacheKey);
  }
};

const practiceService = {
  // Get all practices
  getPractices: async (params = {}) => {
    const response = await api.get('/practices', { params });
    return response;
  },


  // Get practice by ID
  getPractice: async (id) => {
    const response = await api.get(`/practices/${id}`);
    return response;
  },

  // Check practice admin username availability
  checkAdminUsername: async (username, practiceId = null) => {
    const params = { username };
    if (practiceId) params.practiceId = practiceId;
    const response = await api.get('/practices/check-admin-username', { params });
    return response;
  },

  checkAdminEmail: async (email, practiceId = null) => {
    const params = { email };
    if (practiceId) params.practiceId = practiceId;
    const response = await api.get('/practices/check-admin-email', { params });
    return response;
  },

  // Check practice Tax ID (EIN) availability
  checkTaxId: async (taxId, excludePracticeId = null) => {
    const params = { tax_id: taxId };
    if (excludePracticeId) params.excludePracticeId = excludePracticeId;
    const response = await api.get('/practices/check-tax-id', { params });
    return response;
  },

  // Get lightweight practice summary (faster dashboard load)
  getPracticeSummary: async (id, options = {}) => {
    const params = {};
    if (options.auditView) params.auditView = 1;
    const response = await api.get(`/practices/${id}/summary`, { params });
    return response;
  },

  // Create new practice
  createPractice: async (practiceData) => {
    // Check if practiceData is FormData (file upload) or regular object
    const isFormData = practiceData instanceof FormData;
    const config = isFormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};

    const response = await api.post('/practices', practiceData, config);
    return response;
  },

  // Update practice
  updatePractice: async (id, practiceData) => {
    // Check if practiceData is FormData (file upload) or regular object
    const isFormData = practiceData instanceof FormData;
    const config = isFormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};

    const response = await api.put(`/practices/${id}?minimal=0`, practiceData, config);
    return response;
  },

  // Delete practice
  deletePractice: async (id) => {
    const response = await api.delete(`/practices/${id}`);
    return response;
  },

  // Get practice dashboard data
  getPracticeDashboard: async (id) => {
    const response = await api.get(`/practices/${id}/dashboard`);
    return response;
  },

  // Get practice statistics
  getPracticeStats: async (id) => {
    const response = await api.get(`/practices/${id}/stats`);
    return response;
  },

  // Get practice patients
  getPracticePatients: async (id, params = {}) => {
    const cacheKey = getPracticePatientsCacheKey(id, params);
    const generation = practicePatientsCacheGeneration;
    const cached = practicePatientsCache.get(cacheKey);
    if (cached) {
      return { data: cached.data };
    }

    const inFlight = practicePatientsInFlight.get(cacheKey);
    if (inFlight) return inFlight;

    const requestStartedAt = performance.now();
    const promise = api.get(`/practices/${id}/patients`, { params }).then((response) => {
      const elapsedMs = performance.now() - requestStartedAt;
      const serverTimingMs = response?.headers?.['x-patients-timing-ms'];
      const serverDbMs = response?.headers?.['x-patients-db-ms'];
      const payloadBytes = response?.headers?.['x-patients-rows-bytes'];
      console.info(
        `[practiceService.getPracticePatients] practice=${id} page=${params?.page ?? 1} limit=${params?.limit ?? 'default'} ` +
        `client_ms=${elapsedMs.toFixed(1)} server_ms=${serverTimingMs ?? 'n/a'} db_ms=${serverDbMs ?? 'n/a'} rows_bytes=${payloadBytes ?? 'n/a'}`
      );

      storePracticePatientsCache(cacheKey, generation, response.data);
      clearPracticePatientsInFlight(cacheKey, promise);
      return response;
    }).catch((err) => {
      clearPracticePatientsInFlight(cacheKey, promise);
      throw err;
    });

    practicePatientsInFlight.set(cacheKey, promise);
    return promise;
  },

  // Invalidate cached patient lists for one practice or all practices.
  invalidatePracticePatientsCache: (practiceId = null) => {
    practicePatientsCacheGeneration += 1;
    if (!practiceId) {
      practicePatientsCache.clear();
      practicePatientsInFlight.clear();
      return;
    }
    const needle = `:practice:${practiceId}:patients:`;
    Array.from(practicePatientsCache.keys()).forEach((key) => {
      if (key.includes(needle)) {
        practicePatientsCache.delete(key);
      }
    });
    Array.from(practicePatientsInFlight.keys()).forEach((key) => {
      if (key.includes(needle)) {
        practicePatientsInFlight.delete(key);
      }
    });
  },

  // Invalidate program KPI analytics cache (recent / missed / abnormal counts).
  invalidateProgramAnalyticsCache: (practiceId = null) => {
    if (!practiceId) {
      programAnalyticsCache.clear();
      programAnalyticsInFlight.clear();
      return;
    }
    const prefix = `${practiceId}:analytics:`;
    Array.from(programAnalyticsCache.keys()).forEach((key) => {
      if (key.startsWith(prefix)) programAnalyticsCache.delete(key);
    });
    Array.from(programAnalyticsInFlight.keys()).forEach((key) => {
      if (key.startsWith(prefix)) programAnalyticsInFlight.delete(key);
    });
  },

  // Invalidate patient list + analytics for a practice (e.g. after adding readings).
  invalidatePracticePatientCaches: (practiceId = null) => {
    practiceService.invalidatePracticePatientsCache(practiceId);
    practiceService.invalidateProgramAnalyticsCache(practiceId);
  },

  // Get practice providers (optional params e.g. { minimal: true } for dropdowns — id, name, role only)
  getPracticeProviders: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/providers`, { params });
    return response;
  },
  // In practiceService.js
  getPracticePatientsByProgram: async (id, params = {}) => {
    const { program = 'rpm', ...restParams } = params;
    const cacheKey = getPracticePatientsCacheKey(id, { ...restParams, program });
    const generation = practicePatientsCacheGeneration;
    const cached = practicePatientsCache.get(cacheKey);
    if (cached) {
      return { data: cached.data };
    }

    const inFlight = practicePatientsInFlight.get(cacheKey);
    if (inFlight) return inFlight;

    const promise = api.get(`/practices/${id}/patients/program`, {
      params: { ...restParams, program }
    }).then((response) => {
      storePracticePatientsCache(cacheKey, generation, response.data);
      clearPracticePatientsInFlight(cacheKey, promise);
      return response;
    }).catch((err) => {
      clearPracticePatientsInFlight(cacheKey, promise);
      throw err;
    });

    practicePatientsInFlight.set(cacheKey, promise);
    return promise;
  },
  // NEW API method to fetch strict filtered patients for a program tab
  getPracticePatientsFilteredByProgramTab: async (id, params = {}) => {
    const { program = 'rpm', ...restParams } = params;
    const cacheKey = getPracticePatientsCacheKey(id, { ...restParams, program, filtered: true });
    const generation = practicePatientsCacheGeneration;
    const cached = practicePatientsCache.get(cacheKey);
    if (cached) {
      return { data: cached.data };
    }

    const inFlight = practicePatientsInFlight.get(cacheKey);
    if (inFlight) return inFlight;

    const promise = api.get(`/practices/${id}/patients/program-filtered`, {
      params: { ...restParams, program }
    }).then((response) => {
      storePracticePatientsCache(cacheKey, generation, response.data);
      clearPracticePatientsInFlight(cacheKey, promise);
      return response;
    }).catch((err) => {
      clearPracticePatientsInFlight(cacheKey, promise);
      throw err;
    });

    practicePatientsInFlight.set(cacheKey, promise);
    return promise;
  },
  getPracticeProgramPatientAnalytics: async (id, params = {}) => {
    const { program = 'rpm', forceRefresh = false } = params;
    const cacheKey = `${id}:analytics:${program}`;
    if (!forceRefresh) {
      const cached = programAnalyticsCache.get(cacheKey);
      if (cached && Date.now() - cached.ts < PROGRAM_ANALYTICS_CACHE_TTL_MS) {
        return { data: cached.data };
      }

      const inFlight = programAnalyticsInFlight.get(cacheKey);
      if (inFlight) return inFlight;
    } else {
      programAnalyticsCache.delete(cacheKey);
      programAnalyticsInFlight.delete(cacheKey);
    }

    const promise = api.get(`/practices/${id}/patients/program-analytics`, {
      params: { program, ...(forceRefresh ? { refresh: 1 } : {}) }
    })
      .then((response) => {
        programAnalyticsCache.set(cacheKey, { data: response.data, ts: Date.now() });
        programAnalyticsInFlight.delete(cacheKey);
        return response;
      })
      .catch((err) => {
        programAnalyticsInFlight.delete(cacheKey);
        throw err;
      });

    programAnalyticsInFlight.set(cacheKey, promise);
    return promise;
  },
  // Get practice caregivers (optional params e.g. { minimal: true })
  getPracticeCaregivers: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/caregivers`, { params });
    return response;
  },

  // Search practices
  searchPractices: async (query, filters = {}) => {
    const response = await api.get('/practices/search', {
      params: { q: query, ...filters }
    });
    return response;
  },

  // Get practice recent uploads
  getRecentUploads: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/recent-uploads`, { params });
    return response;
  },

  // Get practice missed uploads
  getMissedUploads: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/missed-uploads`, { params });
    return response;
  },

  // Get practice abnormal measurements
  getAbnormalMeasurements: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/abnormal-measurements`, { params });
    return response;
  },

  // Get practice billing reports
  getBillingReports: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/billing-reports`, { params });
    return response;
  },

  // Update practice settings
  updatePracticeSettings: async (id, settings) => {
    const response = await api.put(`/practices/${id}/settings`, settings);
    return response;
  },

  // Get practice schedule and targets
  getScheduleAndTargets: async (id, options = {}) => {
    const cacheKey = getScheduleTargetsCacheKey(id);
    const now = Date.now();
    if (options.skipCache) {
      scheduleTargetsCache.delete(cacheKey);
    }

    const cached = scheduleTargetsCache.get(cacheKey);
    if (cached && cached.expiresAt > now) {
      // Keep the same response shape used by the UI: response.data.success + response.data.data
      return { data: cached.data };
    }

    const inFlight = scheduleTargetsInFlight.get(cacheKey);
    if (inFlight) return inFlight;

    const promise = api.get(`/practices/${id}/schedule-targets`).then((response) => {
      scheduleTargetsCache.set(cacheKey, {
        expiresAt: Date.now() + SCHEDULE_TARGETS_CACHE_TTL_MS,
        data: response.data
      });
      scheduleTargetsInFlight.delete(cacheKey);
      return response;
    }).catch((err) => {
      scheduleTargetsInFlight.delete(cacheKey);
      throw err;
    });

    scheduleTargetsInFlight.set(cacheKey, promise);
    return promise;
  },

  // Update practice schedule and targets
  updateScheduleAndTargets: async (id, data) => {
    const cacheKey = getScheduleTargetsCacheKey(id);
    const response = await api.put(`/practices/${id}/schedule-targets`, data);
    // Invalidate so the next Schedule load is fresh.
    scheduleTargetsCache.delete(cacheKey);
    return response;
  },

  // Preview patients matching current practice schedule/target sections
  getMatchingScheduleTargetPatients: async (id, sections) => {
    const response = await api.post(`/practices/${id}/schedule-targets/matching-patients`, {
      sections
    });
    return response;
  },

  // Apply current practice schedule/target sections to selected patients
  applyScheduleAndTargetsToPatients: async (id, { patientIds, sections, applyMode }) => {
    const response = await api.post(`/practices/${id}/schedule-targets/apply-to-patients`, {
      patientIds,
      sections,
      applyMode
    });
    return response;
  },

  // Import patients to practice
  importPatients: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/practices/${id}/import-patients`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Export practice data
  exportPracticeData: async (id, format = 'csv') => {
    const response = await api.get(`/practices/${id}/export`, {
      params: { format },
      responseType: 'blob'
    });
    return response;
  },

  // Get practice admins
  getPracticeAdmins: async (id) => {
    const response = await api.get(`/practices/${id}/admins`);
    return response;
  },

  // Get system caregivers (optionally filtered by practiceId). High limit so pickers include all assignable users.
  getSystemCaregivers: async (practiceId = null, extraParams = {}) => {
    const params = { page: 1, limit: 2500, ...extraParams };
    if (practiceId) params.practiceId = practiceId;
    const response = await api.get('/system-caregivers', { params });
    return response;
  }
};


export default practiceService;
